import React, { memo, useCallback, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import { DataGrid, GridCellModes } from "@mui/x-data-grid";
import { uniq, has } from "lodash";
import { ENTITY_TYPES } from "../../utils";
import useStableCallback from "../../hooks/useStableCallback";
import usePermissions from "../../hooks/usePermissions";
import getPlanogramItemColumns, {
  POG_ITEM_EDITABLE_FIELDS,
} from "./pogItemTableColumns";
import getOrderItemColumns from "./orderItemTableColumns";
import CustomPagination from "../../components/CustomPagination";
import { validItem } from "../../utils/itemValidator";
import { SCOPES } from "../../permissions/permissions-map";

const PAGE_SIZE = 25;

const DataGridSx = {
  "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
    {
      display: "none",
    },
};

const DataGridContainerSx = { height: 475, width: "100%" };

const DataGridExperimentalFeatures = { newEditingApi: true };

function ItemTable({
  data,
  scope,
  parentId,
  serverSelections = [],
  updateSelections,
  getProcessedRow,
  plannerNotes = [],
  retailerNotes = [],
}) {
  const store = useSelector((store) => store.global.store);
  const entityType = useSelector((store) => store.global.settings.entityType);
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const { hasPermission } = usePermissions();
  const [cellModesModel, setCellModesModel] = useState({});
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const restrictEdit = useMemo(
    () =>
      store?.pending ||
      store?.isArchived ||
      !hasPermission({ scopes: [SCOPES.canEdit] }),
    [store, hasPermission]
  );

  const itemTableColumns = useMemo(() => {
    switch (entityType) {
      case ENTITY_TYPES.planogram:
        return getPlanogramItemColumns(
          getProcessedRow,
          plannerNotes,
          retailerNotes,
          restrictEdit
        );
      case ENTITY_TYPES.orders:
        return getOrderItemColumns();
      default:
        return getPlanogramItemColumns(
          getProcessedRow,
          plannerNotes,
          retailerNotes,
          restrictEdit
        );
    }
  }, [entityType, getProcessedRow, plannerNotes, retailerNotes, restrictEdit]);

  const handleSelectionModelChange = useStableCallback((newModel) => {
    if (adjQtyError) {
      return;
    }
    if (newModel?.length === 1 && newModel?.includes(parentId)) {
      return updateSelections([], parentId, scope);
    }
    const updatedModel = uniq([...newModel, parentId]);
    if (updatedModel.join("") !== serverSelections.join("")) {
      return updateSelections(updatedModel, parentId, scope);
    }
  });

  const handleIsCellEditable = useStableCallback((params) => {
    return serverSelections.includes(params.row.id);
  });

  const handlePageSizeChange = useCallback((newPage) => {
    setPageSize(newPage);
  }, []);

  const handleProcessRowUpdate = useStableCallback(async (updatedRow) => {
    if (adjQtyError) {
      return await Promise.reject();
    }
    getProcessedRow(updatedRow);
    return await Promise.resolve(updatedRow);
  });

  const handleCellClick = useStableCallback((params) => {
    setCellModesModel((prevModel) => {
      if (!params.isEditable) {
        return prevModel;
      }
      if (adjQtyError) {
        const keys = has(prevModel, adjQtyError)
          ? Object.keys(prevModel[adjQtyError] || {}) || []
          : [];
        if (!keys.length) {
          return prevModel;
        }
        return {
          [adjQtyError]: {
            ...keys.reduce(
              (acc, field) => ({
                ...acc,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
            adjqty: {
              mode: GridCellModes.Edit,
            },
          },
        };
      }
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {}
            ),
          }),
          {}
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {}
          ),
          [params.field]: {
            mode: GridCellModes.Edit,
          },
        },
      };
    });
  });

  const handleCellModesModelChange = useStableCallback((newModel) => {
    if (adjQtyError) {
      return;
    }
    setCellModesModel(newModel);
  });

  const isRowSelectable = useStableCallback((params) => {
    if (entityType === ENTITY_TYPES.orders) {
      return validItem(params?.row);
    }
    //TODO: For other entities use condition as needed.
    return true;
  });

  const getEditCellClass = useStableCallback((params) => {
    if (POG_ITEM_EDITABLE_FIELDS.includes(params.field) && params.isEditable) {
      return "oso-editable-field";
    }
    return "";
  });

  return (
    <Box sx={DataGridContainerSx}>
      <DataGrid
        sx={DataGridSx}
        rows={data}
        columns={itemTableColumns}
        checkboxSelection
        disableColumnMenu
        disableSelectionOnClick
        experimentalFeatures={DataGridExperimentalFeatures}
        selectionModel={serverSelections}
        isRowSelectable={isRowSelectable}
        onSelectionModelChange={handleSelectionModelChange}
        processRowUpdate={handleProcessRowUpdate}
        cellModesModel={cellModesModel}
        onCellModesModelChange={handleCellModesModelChange}
        isCellEditable={handleIsCellEditable}
        onCellClick={handleCellClick}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        getCellClassName={getEditCellClass}
        hideFooterSelectedRowCount
        density="compact"
        components={{
          Pagination: CustomPagination,
        }}
      />
    </Box>
  );
}

export default memo(ItemTable);
